import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  H5_HOSTING_NOTICE,
  HOSTING,
  HOSTING_AVAILABILITY,
  HOSTING_RISK_OPTIONS,
  RISK_ALLOWANCE,
  calculateEstimate,
  formatUSD,
  type HostingPlan,
  type Panel,
  type Risk,
} from '../lib/quoteEstimator';
import {
  AVAILABLE_HOSTING_PRICE_MAX,
  HOSTING_CATEGORIES,
  HOSTING_INDUSTRY_RANGES,
  HOSTING_PRICE_MIN,
  PLAN_COUNT,
  isCategoryAvailable,
  type HostingCategory,
} from '../data/hostingPlans';
import { mailtoHref } from '../lib/site';
import PageHeader from '../components/ui/PageHeader';
import Tabs from '../components/ui/Tabs';
import Accordion from '../components/ui/Accordion';
import HScroller from '../components/ui/HScroller';

const choice =
  'flex items-start gap-3 min-h-[44px] px-3 py-2.5 rounded-xl border border-secondary-800 bg-secondary-900/60 text-sm text-secondary-200 cursor-pointer hover:border-secondary-600 has-[:checked]:border-accent-400/60 has-[:checked]:bg-accent-500/10 has-[:disabled]:opacity-60 has-[:disabled]:cursor-not-allowed';

interface FinderState {
  panel: Panel;
  highTraffic: boolean;
  ai: boolean;
  risk: Risk;
}

const INITIAL: FinderState = { panel: 'none', highTraffic: false, ai: false, risk: 0 };

const pct = (r: number) => `${Math.round(r * 100)}%`;

const CategoryPanel: React.FC<{ cat: HostingCategory; state: FinderState; set: (p: Partial<FinderState>) => void }> = ({ cat, state, set }) => {
  const groupId = `plan-${cat.id}`;
  const panel: Panel = !cat.advancedOptions && state.panel === 'advanced' ? 'content' : state.panel;
  const ai = cat.advancedOptions && state.ai;

  const est = useMemo(
    () =>
      calculateEstimate({
        tier: cat.engineTier,
        baseDevelopmentPrice: 0,
        panel,
        aiSelfHealing: ai,
        fairCorpHosting: true,
        highTraffic: state.highTraffic,
        hostingRisk: state.risk,
      }),
    [cat, panel, ai, state.highTraffic, state.risk],
  );

  const plan = est.hostingPlan as HostingPlan;
  const planInfo = cat.plans.find((p) => p.id === plan);
  const rate = RISK_ALLOWANCE[state.risk];
  const riskOption = HOSTING_RISK_OPTIONS[state.risk];

  const inquiry = mailtoHref('hosting', [
    'Hosting inquiry (informational estimate — not a contract)',
    '',
    `Category: ${cat.name}`,
    `Suggested plan: ${plan} — ${formatUSD(est.baseMonthly)}/mo base`,
    `Management interface: ${panel === 'none' ? 'none' : panel === 'content' ? 'standard content-management' : 'advanced application-management'}`,
    `High traffic: ${state.highTraffic ? 'yes' : 'no'}`,
    ...(cat.advancedOptions ? [`AI self-healing (engineering quote, completion requisite if requested): ${ai ? 'requested' : 'no'}`] : []),
    `Hosting risk rating (self-assessed, illustrative): ${riskOption.label}`,
    '',
    'About my site / what I need hosted:',
    '',
  ]);

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
      <div className="space-y-4">
        <p className="text-sm text-secondary-300">
          <span className="font-semibold text-white">{cat.name}.</span> {cat.summary}{' '}
          <span className="text-secondary-400">Typical: {cat.runs}</span>
        </p>

        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-white mb-2">Management interface</legend>
          <label className={choice}>
            <input type="radio" name={`${groupId}-panel`} className="mt-0.5 accent-fuchsia-500" checked={panel === 'none'} onChange={() => set({ panel: 'none' })} />
            <span>None — you or FairCorp edit files directly</span>
          </label>
          <label className={choice}>
            <input type="radio" name={`${groupId}-panel`} className="mt-0.5 accent-fuchsia-500" checked={panel === 'content'} onChange={() => set({ panel: 'content' })} />
            <span>Standard content-management interface — edit public text, menus, prices, images</span>
          </label>
          {cat.advancedOptions && (
            <label className={choice}>
              <input type="radio" name={`${groupId}-panel`} className="mt-0.5 accent-fuchsia-500" checked={panel === 'advanced'} onChange={() => set({ panel: 'advanced' })} />
              <span>Advanced application-management interface — records, accounts, permissions</span>
            </label>
          )}
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-white mb-2">Traffic and operations</legend>
          <label className={choice}>
            <input type="checkbox" className="mt-0.5 accent-fuchsia-500" checked={state.highTraffic} onChange={(e) => set({ highTraffic: e.target.checked })} />
            <span>High-traffic site (self-declared — staff confirm sizing)</span>
          </label>
          {cat.advancedOptions ? (
            <label className={choice}>
              <input type="checkbox" className="mt-0.5 accent-fuchsia-500" checked={ai} onChange={(e) => set({ ai: e.target.checked })} />
              <span>
                AI self-healing{' '}
                <span className="ml-1 align-middle rounded-full border border-accent-400/40 bg-accent-500/10 px-2 py-0.5 text-[11px] font-bold text-accent-200">IN DEVELOPMENT</span>
                <span className="block text-xs text-secondary-400 mt-0.5">If requested, it is built into the site as a completion requisite and priced by an official engineering quote — not a self-serve add-on. Selecting it previews the higher hosting configuration.</span>
              </span>
            </label>
          ) : (
            <p className="text-xs text-secondary-400 px-1">AI self-healing is offered only with the backend, database, and high-reliability categories.</p>
          )}
        </fieldset>

        <div>
          <label htmlFor={`${groupId}-risk`} className="block text-sm font-semibold text-white mb-1.5">
            Hosting risk rating <span className="font-normal text-secondary-400">(illustrative, not approved policy)</span>
          </label>
          <select
            id={`${groupId}-risk`}
            value={state.risk}
            onChange={(e) => set({ risk: Number(e.target.value) as Risk })}
            className="w-full min-h-[44px] px-3 rounded-xl bg-secondary-900 border border-secondary-700 text-secondary-100 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            {HOSTING_RISK_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-xs text-secondary-400">{riskOption.detail}</p>
        </div>
      </div>

      <aside aria-label="Suggested hosting plan" className="lg:sticky lg:top-24 self-start rounded-2xl border border-accent-400/40 bg-accent-500/5 p-4" aria-live="polite">
        <span className="chip-host">Monthly · hosting contract</span>
        <p className="mt-3 flex items-baseline gap-2">
          <span className="text-3xl font-bold text-white">{plan}</span>
          <span className="text-xl font-semibold text-accent-200">{formatUSD(est.baseMonthly)}/mo</span>
        </p>
        <p className="mt-1 text-sm text-secondary-300">
          {planInfo?.label}. <span className="text-secondary-400">Selected for: {planInfo?.when.toLowerCase()}.</span>
        </p>

        {rate === null ? (
          <p className="mt-3 text-sm text-warning-300">F5 — custom security assessment. No automatic monthly total.</p>
        ) : rate > 0 ? (
          <p className="mt-3 text-sm text-secondary-200">
            With the illustrative {pct(rate)} allowance:{' '}
            <span className="font-semibold text-white">{formatUSD(est.monthlyEstimate ?? est.baseMonthly)}/mo</span>
          </p>
        ) : null}
        {est.requiresReview && <p className="mt-2 text-sm text-warning-300">Needs a manual security review before any hosting contract.</p>}

        <div className="mt-4 flex flex-col gap-2">
          <a href={inquiry} className="btn btn-primary">
            Send a hosting inquiry
          </a>
          <Link to="/pricing" className="btn btn-outline">
            Need a build too? Development pricing
          </Link>
        </div>
        <p className="mt-3 text-xs text-secondary-400">Informational estimate only — not a contract. Hosting is separate from any development work.</p>
      </aside>

      <section className="lg:col-span-2" aria-label={`Plans in ${cat.name}`}>
        <h3 className="text-sm font-semibold text-white mb-2">
          {cat.plans.length} plans in this category
        </h3>
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {cat.plans.map((p) => {
            const selected = p.id === plan;
            return (
              <li
                key={p.id}
                aria-current={selected ? 'true' : undefined}
                className={`rounded-xl border px-3 py-2.5 ${selected ? 'border-accent-400/60 bg-accent-500/10' : 'border-secondary-800 bg-secondary-900/50'}`}
              >
                <p className="flex items-baseline justify-between gap-2">
                  <span className="font-bold text-white">{p.id}</span>
                  <span className="font-semibold text-secondary-100">{formatUSD(HOSTING[p.id])}/mo</span>
                </p>
                <p className="text-xs text-secondary-300 mt-0.5">{p.label}</p>
                <p className="text-xs text-secondary-400">{p.when}</p>
                {selected && <p className="text-[11px] font-semibold text-accent-200 mt-1">Your selection</p>}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
};

const UnavailableCategoryPanel: React.FC<{ cat: HostingCategory }> = ({ cat }) => {
  const inquiry = mailtoHref('hosting', [
    'H5 hosting availability inquiry',
    '',
    `Category: ${cat.name}`,
    'I understand that H5a–H5d are not available at this time.',
    '',
    'About my site / what I need hosted:',
    '',
  ]);

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
      <div className="space-y-4">
        <p className="text-sm text-secondary-300">
          <span className="font-semibold text-white">{cat.name}.</span> {cat.summary}{' '}
          <span className="text-secondary-400">Typical: {cat.runs}</span>
        </p>

        <div className="panel p-4 sm:p-6 border-warning-500/40 bg-warning-500/5" role="status">
          <span className="inline-flex rounded-full border border-warning-500/40 bg-warning-500/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-warning-200">
            H5 · not currently offered
          </span>
          <h2 className="mt-3 text-xl font-bold tracking-tight text-white">High-reliability hosting is not available at this time.</h2>
          <p className="mt-2 text-sm text-secondary-300">
            All four H5 plans are approved, but FairCorp is not offering them right now. We are not quoting a monthly H5 price, and the finder cannot build an automatic H5 selection.
          </p>
          <p className="mt-2 text-sm text-secondary-300">
            A T5 development project can still be scoped separately. If you need hosting for it, send an inquiry and we will record your interest and discuss the options when H5 is available.
          </p>
        </div>

        <section aria-label={`Plans in ${cat.name}`}>
          <h3 className="text-sm font-semibold text-white mb-2">
            {cat.plans.length} plans in this category
          </h3>
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {cat.plans.map((p) => (
              <li key={p.id} className="rounded-xl border border-secondary-800 bg-secondary-900/40 px-3 py-2.5">
                <p className="flex items-baseline justify-between gap-2">
                  <span className="font-bold text-white">{p.id}</span>
                  <span className="text-[11px] font-semibold text-warning-300">Not available</span>
                </p>
                <p className="text-xs text-secondary-300 mt-0.5">{p.label}</p>
                <p className="text-xs text-secondary-400">{p.when}</p>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <aside aria-label="Suggested hosting plan" className="lg:sticky lg:top-24 self-start rounded-2xl border border-warning-500/40 bg-warning-500/5 p-4" aria-live="polite">
        <span className="chip-host">H5 · monthly · status</span>
        <p className="mt-3 text-2xl font-bold text-warning-200">Not available at this time</p>
        <p className="mt-2 text-sm text-secondary-300">
          H5a–H5d are not being offered. No monthly estimate is shown for this category.
        </p>
        <div className="mt-4 flex flex-col gap-2">
          <a href={inquiry} className="btn btn-primary">
            Send a hosting inquiry
          </a>
          <Link to="/pricing" className="btn btn-outline">
            Need a build too? Development pricing
          </Link>
        </div>
        <p className="mt-3 text-xs text-secondary-400">Informational only — not a contract. Hosting is separate from any development work.</p>
      </aside>
    </div>
  );
};

const Finder: React.FC = () => {
  const [catId, setCatId] = useState(HOSTING_CATEGORIES[0].id);
  const [state, setState] = useState<FinderState>(INITIAL);
  const set = (p: Partial<FinderState>) => setState((s) => ({ ...s, ...p }));

  return (
    <Tabs
      label="Hosting category"
      value={catId}
      onChange={(id) => {
        setCatId(id);
        const next = HOSTING_CATEGORIES.find((c) => c.id === id)!;
        // Options that do not exist for the new family are cleared so the result never silently carries them.
        if (!next.advancedOptions) setState((s) => ({ ...s, ai: false, panel: s.panel === 'advanced' ? 'content' : s.panel }));
      }}
      tabs={HOSTING_CATEGORIES.map((c) => ({
        id: c.id,
        label: c.short,
        ariaLabel: c.name,
        panel: isCategoryAvailable(c) ? <CategoryPanel cat={c} state={state} set={set} /> : <UnavailableCategoryPanel cat={c} />,
      }))}
    />
  );
};

const Compare: React.FC = () => (
  <div className="space-y-3">
    <p role="note" className="rounded-xl border border-warning-500/40 bg-warning-500/5 px-4 py-3 text-sm text-warning-100">
      <span className="font-semibold">{H5_HOSTING_NOTICE}</span>{' '}
      The four H5 rows stay listed for reference, but no monthly price is offered and the finder will not select them.
    </p>
    <HScroller label="All hosting plans comparison table" trackClassName="rounded-2xl border border-secondary-800">
      <table className="w-full min-w-[34rem] text-sm text-left border-collapse">
        <caption className="sr-only">All {PLAN_COUNT} approved hosting plans, noting that H5 plans are not available at this time</caption>
        <thead className="bg-secondary-900 text-secondary-300">
          <tr>
            <th scope="col" className="sticky left-0 z-10 bg-secondary-900 px-3 py-2.5 font-semibold">Plan</th>
            <th scope="col" className="px-3 py-2.5 font-semibold whitespace-nowrap">Per month</th>
            <th scope="col" className="px-3 py-2.5 font-semibold">Option</th>
            <th scope="col" className="px-3 py-2.5 font-semibold">Selected for</th>
          </tr>
        </thead>
        {HOSTING_CATEGORIES.map((c) => (
          <tbody key={c.id}>
            <tr className="bg-secondary-900/80">
              <th colSpan={4} scope="colgroup" className="sticky left-0 px-3 py-2 text-left text-xs uppercase tracking-wide text-accent-200">
                <span className="block max-w-[calc(100vw-4rem)] whitespace-normal">{c.name}</span>
              </th>
            </tr>
            {c.plans.map((p) => {
              const available = HOSTING_AVAILABILITY[p.id];
              return (
                <tr key={p.id} className={`border-t border-secondary-800 ${available ? '' : 'bg-warning-500/[0.03]'}`}>
                  <th scope="row" className="sticky left-0 z-10 bg-secondary-950 px-3 py-2.5 font-bold text-white">
                    {p.id}
                    {!available && <span className="mt-0.5 block text-[11px] font-semibold uppercase tracking-wide text-warning-300">Not available</span>}
                  </th>
                  <td className={`px-3 py-2.5 font-semibold whitespace-nowrap ${available ? 'text-white' : 'text-warning-300'}`}>
                    {available ? formatUSD(HOSTING[p.id]) : 'Not available'}
                    <span className="mt-1 block text-xs font-normal text-secondary-400">
                      (industry avg. {HOSTING_INDUSTRY_RANGES[p.id]})
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-secondary-200">{p.label}</td>
                  <td className="px-3 py-2.5 text-secondary-300">{available ? p.when : 'H5 hosting is not available at this time.'}</td>
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
    </HScroller>
  </div>
);

const Plans: React.FC = () => (
  <div className="page-inner pb-10">
    <PageHeader
      kicker={<span className="chip-host">Hosting · monthly, separate from development</span>}
      title="Hosting plans"
      lead={
        <>
          {PLAN_COUNT} approved monthly plans. H1–H4 are available now, from {formatUSD(HOSTING_PRICE_MIN)}–{formatUSD(AVAILABLE_HOSTING_PRICE_MAX)}/mo. H5 hosting is not available at this time. Choose what you run — no development questions needed. Building something new?{' '}
          <Link to="/pricing" className="text-primary-300 underline underline-offset-4 hover:text-primary-200">
            See development pricing
          </Link>
          .
        </>
      }
    />

    <Tabs
      label="Hosting plan views"
      size="md"
      tabs={[
        { id: 'compare', label: `Compare all ${PLAN_COUNT}`, panel: <Compare /> },
        { id: 'find', label: 'Find my plan', panel: <Finder /> },
      ]}
    />

    <section aria-labelledby="hosting-infrastructure" className="mt-8 panel p-4 sm:p-6 border-primary-400/30 bg-primary-500/5">
      <h2 id="hosting-infrastructure" className="text-lg font-bold text-white">Why our lower-tier prices sit above market averages</h2>
      <p className="mt-2 text-sm text-secondary-300">
        All hosting is handled on FairCorp&apos;s own managed infrastructure. Your source code is never served from large corporate cloud infrastructures.
        The industry ranges above are commodity/shared-hosting benchmarks; FairCorp plans include managed servers, TLS everywhere, WAF protection,
        backups, health checks, and direct support, which is why even the entry tiers cost more than a typical $3–$15 shared-hosting plan.
      </p>
    </section>

    <h2 className="mt-8 mb-3 text-lg font-bold text-white">Hosting details and limits</h2>
    <Accordion
      multiple
      items={[
        {
          id: 'covers',
          title: 'What FairCorp hosting covers',
          content: (
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Apache-backed hosting on hardened Ubuntu servers, with automated health checks, log rotation, and backup schedules.</li>
              <li>TLS everywhere, security headers, Fail2Ban intrusion prevention, and ModSecurity WAF protection.</li>
              <li>React single-page apps, static sites, or full-stack apps — we handle Apache virtual hosts, SSL certificates, and CI/CD integration.</li>
            </ul>
          ),
        },
        {
          id: 'select',
          title: 'How a plan is chosen',
          content: (
            <p>
              The category sets the plan family; options inside it (a content-management interface, an advanced interface, AI self-healing, high traffic)
              move you to the larger plan in that family. The letter and number mean nothing outside hosting: <strong className="text-white">T3A/T3B are development tiers, H3a/H3b are hosting configurations</strong>.
              H3b costs $50 (vs H3a $35) because running content management inside a graphically complex site costs more to operate.
            </p>
          ),
        },
        {
          id: 'contracts',
          title: 'Contracts, ownership, and backups',
          content: (
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Development and hosting are separate purchases: host elsewhere, bring an external build to us, or leave hosting without forfeiting your site.</li>
              <li>Small text, content, hours, image, and styling fixes are free at every tier — no hosting purchase required.</li>
              <li>Commissioned source code and accumulated data are yours to transfer.</li>
              <li>Eligible recovery backups are retained up to six months after hosting ends — a transfer window, not extended hosting.</li>
            </ul>
          ),
        },
        {
          id: 'limits',
          title: 'Limits and open items',
          content: (
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Base monthly prices are approved. The F-risk allowances (0 / 5 / 15 / 30%) are illustrative placeholders, and F5 is always a custom security assessment.</li>
              <li>H5a–H5d are approved but <strong className="text-white">not available at this time</strong>. The finder shows that status instead of making an automatic H5 selection or quoting a monthly H5 price.</li>
              <li>“High traffic” is self-declared; no traffic threshold is published, so staff confirm sizing.</li>
              <li>AI self-healing, if requested, is built into the site as a completion requisite and priced by an official engineering quote. It is not a self-serve add-on, and it is not offered on T1–T3.</li>
              <li>This site does not publish uptime, support-hours, or recovery-time commitments. Ask in your inquiry.</li>
              <li>Third-party fees (provider subscriptions, transaction charges) are never part of a hosting plan price.</li>
            </ul>
          ),
        },
      ]}
    />

  </div>
);

export default Plans;
